import { AppData, Lightning } from '@lightningjs/sdk';
import ChannelTile from 'components/common/tiles/ChannelTile';
import { ExpandableList } from 'components/common/ExpandableList';
import HorizontalFeaturedTile from 'components/common/tiles/HorizontalFeaturedTile';
import PortraitTile from 'components/common/tiles/PortraitTile';
import Carousel, {
  TileListTypes,
  ROW_LIST_OFFSET_Y,
  CarouselTemplateSpec,
  isFeaturedListType,
} from 'components/common/carousels/Carousel';
import VerticalFeaturedTile from 'components/common/tiles/VerticalFeaturedTile';
import ContinueTile from 'components/common/tiles/ContinueTile';
import VideoTile from 'components/common/tiles/VideoTile';
import {
  ContentHubLaneItem,
  Lane,
  LiveEventLaneItem,
  Swimlanes,
  VideoLaneItem,
} from 'services/cwData';
import {
  ChannelImages,
  ImageKey,
  LiveEventImages,
  PlayableMediaImages,
} from 'types/api/media';
import { constants } from 'aliases';
import { IndexData, LightningBuilder } from 'types/lightning';
import {
  WatchHistoryCarousel,
  WatchHistoryCarouselTemplateSpec,
} from 'components/common/carousels/WatchHistoryCarousel';
import DefaultCarousel from 'components/common/carousels/DefaultCarousel';
import {
  HoverableComponent,
  HoverableComponentSignalMap,
  HoverableComponentTypeConfig,
} from 'components/common/HoverableComponent';
import SquareContentHubTile from 'components/common/tiles/SquareContentHubTile';
import VerticalContentHubTile from 'components/common/tiles/VerticalContentHubTile';
import HorizontalContentHubTile from 'components/common/tiles/HorizontalContentHubTile';
import LiveEventTile from 'components/common/tiles/LiveEventTile';
import BillboardCarousel from 'components/common/BillboardCarousel';
import { LiveEventCarousel } from 'components/common/carousels/LiveEventCarousel';
import FeaturedCarousel from 'components/common/carousels/FeaturedCarousel';
import BezelContentHubTile from 'components/common/tiles/BezelContentHubTile';
import BezelButtonCarousel from 'components/common/carousels/BezelButtonCarousel';
import { DirectionsType, isHorizontalDirection } from 'support/keyEventUtils';

const NAVBAR_WIDTH = constants.sizing.navbar.collapsedWidth;
const SWIMLANES_SPACING = 15;
const CONTENT_PADDING_X = 20;
const CONTENT_PADDING_H = 300;
export const SWIMLANES_HEIGHT = 1080;

const HIGHLIGHT_POSITION_OFFSET = constants.ui.highlightPositionOffset;

export interface CarouselSectionTemplateSpec
  extends Lightning.Component.TemplateSpec {
  data: Swimlanes | null;
  isNavBarVisible: boolean;

  Wrapper: {
    Swimlanes: typeof ExpandableList;
  };
}

interface CarouselSectionSignalMap extends HoverableComponentSignalMap {
  scrollToPromo(): void;
  scrollToContent(options?: { index?: number; scrollTarget?: number }): void;
  firstLaneScroll(options?: {
    scrollTarget?: number;
    laneHeight?: number;
  }): void;
}

interface CarouselSectionTypeConfig extends HoverableComponentTypeConfig {
  SignalMapType: CarouselSectionSignalMap;
}

export default class CarouselSection
  extends HoverableComponent<
    CarouselSectionTemplateSpec,
    CarouselSectionTypeConfig
  >
  implements
    Lightning.Component.ImplementTemplateSpec<CarouselSectionTemplateSpec>
{
  private _data: CarouselSectionTemplateSpec['data'] = null;

  // Internal properties
  private _continueLaneIndex: number | undefined;
  private _continueLaneAdded = false;
  private _continueSizedRowList:
    | Lightning.Element.PatchTemplate<WatchHistoryCarouselTemplateSpec>
    | undefined;
  private _preventContinueRowScroll = false;
  private _isNavBarVisible = true;
  private _externalIndexOffset = 0;

  private _Wrapper = this.getByRef('Wrapper')!;
  private _Swimlanes = this._Wrapper.getByRef('Swimlanes')!;

  get data() {
    return this._data;
  }

  set data(data: CarouselSectionTemplateSpec['data']) {
    this._data = data;

    this._continueLaneIndex = undefined;
    this._continueLaneAdded = false;
    this._continueSizedRowList = undefined;

    this.updateSwimlanes();
  }

  get index() {
    const mainIndex = this._Swimlanes.index;
    const crossIndex = this._Swimlanes.items[mainIndex]?.index;
    return { mainIndex, crossIndex };
  }

  setIndices(mainIndex: number, crossIndex: number) {
    const updatedIndex = mainIndex + this._externalIndexOffset;
    this._externalIndexOffset = 0;
    if (
      updatedIndex !== this._Swimlanes.index &&
      updatedIndex <= this._Swimlanes.length - 1 &&
      updatedIndex >= 0
    ) {
      this._Swimlanes.index = updatedIndex;
    }

    const row = this._Swimlanes.items[updatedIndex] as Carousel;
    if (crossIndex && row.laneItems && crossIndex !== row.index) {
      row.index = crossIndex;
    }
  }

  override onSwipeAction = (direction: DirectionsType, strength: number) => {
    if (isHorizontalDirection(direction)) return;
    if (!this.hovered) return;

    const newIndex = Math.max(
      0,
      this.index.mainIndex + strength * (direction === 'Down' ? 1 : -1),
    );

    this._Swimlanes.setIndex(newIndex);
  };

  get currentRow() {
    return this._Swimlanes.currentItem;
  }

  get scrollHeight() {
    return (
      SWIMLANES_HEIGHT * 0.75 +
      this._Swimlanes.itemWrappers[this._Swimlanes.length - 1].y
    );
  }

  get swimlaneWrapperY() {
    return this._Swimlanes.wrapper.y;
  }

  get isNavBarVisible() {
    return this._isNavBarVisible;
  }

  set isNavBarVisible(value: boolean) {
    this._isNavBarVisible = value;
  }

  static override _template(): Lightning.Component.Template<CarouselSectionTemplateSpec> {
    return {
      w: 1920,
      h: 1080,
      Wrapper: {
        w: 1920 * 2,
        h: 1080 + CONTENT_PADDING_H,
        x: -1920 + HIGHLIGHT_POSITION_OFFSET + CONTENT_PADDING_X, // No horizontal clipping required
        clipping: true,
        Swimlanes: {
          x: 1920 - HIGHLIGHT_POSITION_OFFSET,
          y: -HIGHLIGHT_POSITION_OFFSET,
          w: 1920,
          h: SWIMLANES_HEIGHT,
          type: ExpandableList,
          direction: 'column',
          spacing: SWIMLANES_SPACING,
          passSignals: {
            onListFocus: true,
          },
        },
      },
    };
  }

  checkFirstRowScroll() {
    // If billboard + first row height is greater than 1080, scroll to first lane to avoid cut-off
    const billboardHeight = BillboardCarousel.height;
    const firstRowHeight = this.currentRow.h;
    const totalHeight = billboardHeight + firstRowHeight;
    if (this.currentRow.laneIndex === 1 && totalHeight > 1080) {
      this.handleFirstLaneScroll();
    }
  }

  /**
   * Scrolls to top of the list.
   * Returns true if already at top
   */
  scrollToTop() {
    if (this._Swimlanes.index === 0) return true;
    this._Swimlanes.setIndex(0);
  }

  onCarouselHeightChanged() {
    this._Swimlanes.reposition();
  }

  override _setup() {
    super._setup();
    this._Swimlanes.scroll = this.handleSwimlaneScroll.bind(this);
  }

  override _getFocused() {
    return this._Swimlanes;
  }

  override _handleHover(target: Lightning.Element) {
    if (target === this._Wrapper || AppData?.device.hoverDisabled())
      return true;
    return super._handleHover(target);
  }

  override _handleUnhover(target: Lightning.Element) {
    if (target === this._Wrapper) return true;
    return super._handleUnhover(target);
  }

  private updateSwimlanes() {
    if (!this.data) return;

    const items: LightningBuilder<
      | typeof DefaultCarousel
      | typeof WatchHistoryCarousel
      | typeof LiveEventCarousel
      | typeof BezelButtonCarousel,
      CarouselTemplateSpec
    >[] = [];

    let skippedLanesCount = 0;
    this.data.lanes.forEach((lane, laneIndex) => {
      const contentIndex = laneIndex - skippedLanesCount;

      if (lane.laneType === 'promos') {
        // Skip promo lane (Handled by content hub page)
        skippedLanesCount++;
      } else if (lane.laneType === 'continue-watching') {
        this._continueLaneIndex = items.length;
        this._continueSizedRowList = this.buildContinueWatchingRowList(
          lane as Lane,
          contentIndex,
        );
        if (this._continueSizedRowList?.laneItems?.length) {
          items.push(this._continueSizedRowList as WatchHistoryCarousel);
          this._continueLaneAdded = true;
          this._preventContinueRowScroll = true;
        } else {
          skippedLanesCount++;
        }
      } else if (lane.laneType === 'events-list') {
        const rowList = this.buildLiveEventRowList(lane as Lane, contentIndex);
        if (rowList) items.push(rowList);
      } else if (
        lane.itemType === 'content-hub' &&
        lane.presentation === 'cw-bezel'
      ) {
        const rowList = this.buildBezelExploreRowList(
          lane as Lane,
          contentIndex,
        );
        if (rowList) items.push(rowList as any);
      } else {
        const rowList = this.setupSizedRowList(lane as Lane, contentIndex);
        if (rowList) items.push(rowList);
      }
    });

    this._Swimlanes.items = items;
  }

  private handleSwimlaneScroll(
    itemWrapper: Lightning.Component,
    indexData: IndexData,
  ) {
    const { index } = indexData;

    if (index === 0) {
      this.handleFirstLaneScroll();
    } else {
      const scrollTarget =
        -this._Swimlanes.scrollTransition.targetValue + SWIMLANES_HEIGHT;
      this.signal('scrollToContent', { index, scrollTarget });
    }

    let listY = 0;
    if (index > 0) {
      // Scroll by the amount of continue watching row (add/remove) to keep on current selection on center.
      if (this._preventContinueRowScroll) {
        const direction = this._continueLaneAdded ? 1 : -1;
        const offset =
          ((this._continueSizedRowList!.h as number) + SWIMLANES_SPACING) *
          direction;

        listY = this._Swimlanes.lastY + offset;
      } else {
        const { h: selectedItemH, y: selectedItemY } = itemWrapper;
        const selectedItemCenter = selectedItemH / 2;
        const swimlanesCenter = SWIMLANES_HEIGHT / 2;

        listY = -(selectedItemY + selectedItemCenter - swimlanesCenter);
      }
    }

    this._preventContinueRowScroll = false;

    this._Swimlanes.lastY = listY;
    return listY;
  }

  private handleFirstLaneScroll() {
    const scrollTarget =
      -this._Swimlanes.scrollTransition.targetValue + SWIMLANES_HEIGHT;

    const firstLaneWrapper = this._Swimlanes.itemWrappers[0];

    this.signal('firstLaneScroll', {
      scrollTarget,
      laneHeight: firstLaneWrapper?._h,
    });
  }

  private setupSizedRowList(lane: Lane, laneIndex: number) {
    let listType: TileListTypes;
    let imageKey: ImageKey;
    let expandedImageKey: keyof PlayableMediaImages | undefined;

    if (lane.itemType === 'show') {
      if (lane.presentation === 'vertical-expanding') {
        listType = VerticalFeaturedTile;
        imageKey = 'image_vertical';
        expandedImageKey = 'image_vertical_featured';
      } else if (lane.presentation === 'horizontal-expanding') {
        listType = HorizontalFeaturedTile;
        imageKey = 'image_promo';
        expandedImageKey = 'image_show_featured';
      } else {
        listType = PortraitTile;
        imageKey = 'image_vertical';
      }
    } else if (lane.itemType === 'video') {
      listType = VideoTile;
      imageKey = 'thumbnail';
    } else if (lane.itemType === 'channel') {
      listType = ChannelTile;
      imageKey = 'homeThumbnail';
    } else if (lane.itemType === 'content-hub') {
      if (lane.presentation === 'square') {
        listType = SquareContentHubTile;
      } else if (lane.presentation === 'vertical') {
        listType = VerticalContentHubTile;
      } else {
        listType = HorizontalContentHubTile;
      }
      imageKey = 'thumbnail';
    } else if (lane.itemType === 'event') {
      listType = LiveEventTile;
      imageKey = 'eventThumbnail';
    } else {
      return null;
    }

    return this.buildSizedRowList(
      lane,
      laneIndex,
      listType,
      imageKey,
      expandedImageKey,
    );
  }

  private getCommonRowProps(
    lane: Lane,
    laneIndex: number,
    listType: TileListTypes,
  ) {
    return {
      listType,
      w: 1920 - NAVBAR_WIDTH,
      h: listType.height + ROW_LIST_OFFSET_Y,
      label: lane.title,
      itemWidth: listType.width,
      expandedItemWidth: listType.expandedWidth,
      laneIndex,
      navBarOffset: this.x + CONTENT_PADDING_X,
      isNavBarVisible: this.isNavBarVisible,
    };
  }

  private buildSizedRowList(
    lane: Lane,
    laneIndex: number,
    listType: TileListTypes,
    imageKey:
      | keyof PlayableMediaImages
      | keyof ChannelImages
      | keyof LiveEventImages,
    expandedImageKey?: keyof PlayableMediaImages,
  ) {
    const items = lane.items;
    const props = this.getCommonRowProps(lane, laneIndex, listType);

    return {
      ...props,
      type: isFeaturedListType(listType) ? FeaturedCarousel : DefaultCarousel,
      imageKey,
      expandedImageKey,
      laneItems: items,
      signals: {
        onCarouselHeightChanged: () => this.onCarouselHeightChanged(),
      },
    };
  }

  private buildContinueWatchingRowList(lane: Lane, laneIndex: number) {
    const props = this.getCommonRowProps(lane, laneIndex, ContinueTile);
    return {
      ...props,
      type: WatchHistoryCarousel,
      laneIndex,
      laneItems: lane.items as VideoLaneItem[],
    };
  }

  private buildLiveEventRowList(lane: Lane, laneIndex: number) {
    const items = lane.items as LiveEventLaneItem[];
    const props = this.getCommonRowProps(lane, laneIndex, LiveEventTile);

    return {
      ...props,
      type: LiveEventCarousel,
      laneItems: items,
    };
  }

  private buildBezelExploreRowList(lane: Lane, laneIndex: number) {
    const items = lane.items as ContentHubLaneItem[];
    const props = this.getCommonRowProps(lane, laneIndex, BezelContentHubTile);

    return {
      ...props,
      type: BezelButtonCarousel,
      laneItems: items,
    };
  }

  updateContinueLane(continueLane: Lane) {
    const continueLaneIndex = this._continueLaneIndex;
    const continueLaneItems = continueLane.items as VideoLaneItem[];
    this._externalIndexOffset = 0;

    if (
      continueLaneIndex === undefined ||
      continueLane.laneType !== 'continue-watching'
    )
      return;

    if (continueLaneItems?.length) {
      if (!this._continueLaneAdded) {
        // Continue lane has not been added, we have to update data and update all lanes
        this._preventContinueRowScroll = true;
        this._continueLaneAdded = true;

        // we know this is defined because _continueLaneAdded is defined
        const dataIndex = this._data!.lanes.findIndex(
          lane => lane.laneType === 'continue-watching',
        )!;

        this._data!.lanes[dataIndex] = continueLane;

        // Save the current index before replacing data
        const previousIndex = this._Swimlanes.index;

        // trigger data setter to update swimlanes
        this.data = this._data;
        this._externalIndexOffset = previousIndex >= continueLaneIndex ? 1 : 0; // if an index is passed in it will need to be increased

        // Recover the last index
        const updatedIndex = previousIndex + this._externalIndexOffset;

        this._Swimlanes.setIndex(
          Math.min(updatedIndex, this._Swimlanes.length - 1),
        );
      } else {
        const continueLane = this._Swimlanes.items[continueLaneIndex];
        continueLane.laneItems = continueLaneItems;
      }
    } else if (this._continueLaneAdded) {
      // Removing a row will attempt to scroll to the last known index rather than row's adjusted position
      // Set the future index position while disabling scrolling
      this.setIndexWithoutScrolling(this._Swimlanes.index - 1);
      this._preventContinueRowScroll = true;

      this._Swimlanes.removeAt(continueLaneIndex);
      this._continueLaneAdded = false;
      this._Swimlanes.setItemsIndex();
      this._externalIndexOffset = -1; // if an index is passed in it will need to be decreased
    }
  }

  // Workaround to avoid triggering internal scrolling behaviors for special cases (row deletion)
  setIndexWithoutScrolling(index: number) {
    if (index < 0) return;

    this._Swimlanes.scroll = null;
    this._Swimlanes.index = index;
    this._Swimlanes.scroll = this.handleSwimlaneScroll.bind(this);
  }
}
